import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format, addDays, isBefore, startOfToday } from 'date-fns';
import { it } from 'date-fns/locale';
import { Calendar as CalendarIcon, Clock, Check, ArrowRight, ArrowLeft, Smartphone } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_BOOKING_DAYS_AHEAD = 30;

const TimeSlotsPanel = ({ selectedDate, loading, availableSlots, selectedSlot, onSelectSlot }) => {
    if (!selectedDate) {
        return (
            <div className="h-64 flex items-center justify-center text-muted bg-surface-elevated rounded-lg border border-border">
                Seleziona una data
            </div>
        );
    }
    if (loading) {
        return (
            <div className="h-64 flex items-center justify-center bg-surface-elevated rounded-lg border border-border">
                <span className="animate-pulse">Caricamento...</span>
            </div>
        );
    }
    if (availableSlots.length === 0) {
        return (
            <div className="h-64 flex items-center justify-center text-muted bg-surface-elevated rounded-lg border border-border">
                Nessuno slot disponibile
            </div>
        );
    }
    return (
        <div className="grid grid-cols-3 gap-2 bg-surface-elevated rounded-lg p-3 border border-border">
            {availableSlots.map((slot) => (
                <button
                    key={slot}
                    onClick={() => onSelectSlot(slot)}
                    className={`time-slot ${selectedSlot === slot ? 'selected' : ''}`}
                    data-testid={`slot-${slot}`}
                >
                    <Clock className="w-3 h-3 inline mr-1" />
                    {slot}
                </button>
            ))}
        </div>
    );
};

const BookingPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const preselectedService = searchParams.get('service');

    const [step, setStep] = useState(1);
    const [services, setServices] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [selectedService, setSelectedService] = useState(preselectedService || '');
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState('');
    const [formData, setFormData] = useState({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        device_model: '',
        notes: ''
    });

    // Update form data when user changes
    useEffect(() => {
        if (user) {
            setFormData(prev => ({
                ...prev,
                customer_name: prev.customer_name || user.name || '',
                customer_email: prev.customer_email || user.email || ''
            }));
        }
    }, [user]);

    useEffect(() => {
        const fetchServices = async () => {
            try {
                const response = await axios.get(`${API}/api/repair-services`);
                setServices(response.data);
            } catch {
                toast.error('Errore nel caricamento dei servizi');
            }
        };
        fetchServices();
    }, []);

    useEffect(() => {
        if (selectedDate) {
            const fetchSlots = async () => {
                setLoading(true);
                try {
                    const dateStr = format(selectedDate, 'yyyy-MM-dd');
                    const response = await axios.get(`${API}/api/available-slots?date=${dateStr}`);
                    setAvailableSlots(response.data.available_slots);
                } catch {
                    toast.error('Errore nel caricamento degli slot');
                } finally {
                    setLoading(false);
                }
            };
            fetchSlots();
            setSelectedSlot('');
        }
    }, [selectedDate]);

    const handleSubmit = async () => {
        if (!user) {
            toast.error('Devi effettuare il login per prenotare');
            navigate('/login');
            return;
        }

        if (!formData.customer_phone || !formData.device_model) {
            toast.error('Compila tutti i campi obbligatori');
            return;
        }

        setSubmitting(true);
        try {
            const bookingData = {
                service_id: selectedService,
                date: format(selectedDate, 'yyyy-MM-dd'),
                time_slot: selectedSlot,
                ...formData
            };

            await axios.post(`${API}/api/repair-bookings`, bookingData);

            toast.success('Prenotazione confermata!');
            navigate('/prenotazioni');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nella prenotazione');
        } finally {
            setSubmitting(false);
        }
    };

    const selectedServiceData = services.find(s => s.id === selectedService);

    const disabledDays = (date) => {
        return isBefore(date, startOfToday()) || date.getDay() === 0; // Disable past dates and Sundays
    };

    return (
        <div className="min-h-screen py-8">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-3xl font-bold mb-2">Prenota Riparazione</h1>
                <p className="text-muted mb-8">Scegli il servizio, la data e l'orario preferito</p>

                {/* Progress Steps */}
                <div className="flex items-center justify-center mb-8">
                    {[1, 2, 3].map((s) => (
                        <React.Fragment key={s}>
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                                    step >= s ? 'bg-primary text-white' : 'bg-surface border border-border text-muted'
                                }`}
                            >
                                {step > s ? <Check className="w-5 h-5" /> : s}
                            </div>
                            {s < 3 && (
                                <div className={`w-20 h-1 mx-2 rounded ${step > s ? 'bg-primary' : 'bg-border'}`} />
                            )}
                        </React.Fragment>
                    ))}
                </div>

                {/* Step 1: Select Service */}
                {step === 1 && (
                    <div className="bg-surface border border-border rounded-xl p-6 animate-fade-in">
                        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                            <Smartphone className="w-5 h-5 text-primary" />
                            Seleziona il servizio
                        </h2>
                        <Select value={selectedService} onValueChange={setSelectedService}>
                            <SelectTrigger className="bg-surface-elevated border-border" data-testid="service-select">
                                <SelectValue placeholder="Scegli un servizio" />
                            </SelectTrigger>
                            <SelectContent className="bg-surface border-border max-h-80">
                                {services.map((service) => (
                                    <SelectItem key={service.id} value={service.id}>
                                        <div className="flex justify-between items-center w-full">
                                            <span>{service.name}</span>
                                            <span className="text-primary font-semibold ml-4">
                                                €{service.price.toFixed(2)}
                                            </span>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {selectedServiceData && (
                            <div className="mt-4 p-4 bg-surface-elevated rounded-lg">
                                <h3 className="font-semibold">{selectedServiceData.name}</h3>
                                <p className="text-sm text-muted mt-1">{selectedServiceData.description}</p>
                                <div className="flex justify-between mt-2 text-sm">
                                    <span className="text-muted">Durata: ~{selectedServiceData.duration_minutes} min</span>
                                    <span className="text-primary font-bold">€{selectedServiceData.price.toFixed(2)}</span>
                                </div>
                            </div>
                        )}

                        <Button
                            className="w-full mt-6 btn-primary"
                            disabled={!selectedService}
                            onClick={() => setStep(2)}
                            data-testid="step1-next"
                        >
                            Continua
                            <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                    </div>
                )}

                {/* Step 2: Select Date and Time */}
                {step === 2 && (
                    <div className="bg-surface border border-border rounded-xl p-6 animate-fade-in">
                        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                            <CalendarIcon className="w-5 h-5 text-primary" />
                            Seleziona data e orario
                        </h2>

                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Calendar */}
                            <div>
                                <Label className="mb-2 block">Data</Label>
                                <div className="bg-surface-elevated rounded-lg p-3 border border-border">
                                    <Calendar
                                        mode="single"
                                        selected={selectedDate}
                                        onSelect={setSelectedDate}
                                        disabled={disabledDays}
                                        locale={it}
                                        fromDate={startOfToday()}
                                        toDate={addDays(new Date(), MAX_BOOKING_DAYS_AHEAD)}
                                        className="rounded-md"
                                        data-testid="booking-calendar"
                                    />
                                </div>
                            </div>

                            {/* Time Slots */}
                            <div>
                                <Label className="mb-2 block">Orario</Label>
                                <TimeSlotsPanel
                                    selectedDate={selectedDate}
                                    loading={loading}
                                    availableSlots={availableSlots}
                                    selectedSlot={selectedSlot}
                                    onSelectSlot={setSelectedSlot}
                                />
                            </div>
                        </div>

                        <div className="flex gap-4 mt-6">
                            <Button
                                variant="outline"
                                className="flex-1 border-border"
                                onClick={() => setStep(1)}
                            >
                                <ArrowLeft className="mr-2 w-4 h-4" />
                                Indietro
                            </Button>
                            <Button
                                className="flex-1 btn-primary"
                                disabled={!selectedDate || !selectedSlot}
                                onClick={() => setStep(3)}
                                data-testid="step2-next"
                            >
                                Continua
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Step 3: Contact Info */}
                {step === 3 && (
                    <div className="bg-surface border border-border rounded-xl p-6 animate-fade-in">
                        <h2 className="text-xl font-semibold mb-4">I tuoi dati</h2>

                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="customer_name">Nome completo *</Label>
                                <Input
                                    id="customer_name"
                                    value={formData.customer_name}
                                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                                    className="bg-surface-elevated border-border mt-1"
                                    data-testid="customer-name"
                                />
                            </div>
                            <div>
                                <Label htmlFor="customer_email">Email *</Label>
                                <Input
                                    id="customer_email"
                                    type="email"
                                    value={formData.customer_email}
                                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                                    className="bg-surface-elevated border-border mt-1"
                                    data-testid="customer-email"
                                />
                            </div>
                            <div>
                                <Label htmlFor="customer_phone">Telefono *</Label>
                                <Input
                                    id="customer_phone"
                                    value={formData.customer_phone}
                                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                                    placeholder="+39 333 1234567"
                                    className="bg-surface-elevated border-border mt-1"
                                    data-testid="customer-phone"
                                />
                            </div>
                            <div>
                                <Label htmlFor="device_model">Modello dispositivo *</Label>
                                <Input
                                    id="device_model"
                                    value={formData.device_model}
                                    onChange={(e) => setFormData({ ...formData, device_model: e.target.value })}
                                    placeholder="es. iPhone 13 Pro, Samsung S22"
                                    className="bg-surface-elevated border-border mt-1"
                                    data-testid="device-model"
                                />
                            </div>
                            <div>
                                <Label htmlFor="notes">Note aggiuntive</Label>
                                <Textarea
                                    id="notes"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    placeholder="Descrivi il problema..."
                                    className="bg-surface-elevated border-border mt-1"
                                    rows={3}
                                    data-testid="booking-notes"
                                />
                            </div>
                        </div>

                        {/* Summary */}
                        <div className="mt-6 p-4 bg-surface-elevated rounded-lg border border-primary/30">
                            <h3 className="font-semibold mb-2">Riepilogo prenotazione</h3>
                            <div className="text-sm space-y-1 text-muted">
                                <p>Servizio: <span className="text-white">{selectedServiceData?.name}</span></p>
                                <p>Data: <span className="text-white">{selectedDate && format(selectedDate, 'dd MMMM yyyy', { locale: it })}</span></p>
                                <p>Orario: <span className="text-white">{selectedSlot}</span></p>
                                <p className="text-primary font-bold text-lg mt-2">
                                    Totale: €{selectedServiceData?.price.toFixed(2)}
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-4 mt-6">
                            <Button
                                variant="outline"
                                className="flex-1 border-border"
                                onClick={() => setStep(2)}
                            >
                                <ArrowLeft className="mr-2 w-4 h-4" />
                                Indietro
                            </Button>
                            <Button
                                className="flex-1 btn-primary"
                                onClick={handleSubmit}
                                disabled={submitting || !formData.customer_phone || !formData.device_model}
                                data-testid="confirm-booking"
                            >
                                {submitting ? 'Prenotazione...' : 'Conferma prenotazione'}
                                <Check className="ml-2 w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BookingPage;
